import axiosInstance from 'config/axiosInstance';
import { MAX_PAGE_SIZE } from 'config/constant';

type Page<T> = { count: number; next: string | null; results: T[] };

/**
 * BE-21（2026-10-11 PM 判断）。管理画面の全件取得で読む件数の上限。
 * 100件ずつで最大50回の要求。これを超える一覧は読まずに止め、画面に案内を出す。
 */
export const FETCH_ALL_LIMIT = 5000;

/** 全件取得が上限で止まったことを画面へ知らせるイベントの名前（detail は { count, limit }）。 */
export const FETCH_ALL_LIMIT_EVENT = 'yometel:fetch-all-limit';

/** 全件取得の件数が上限を超えた。count はサーバーが返した件数（途中で増えたときは読んだ件数以上）。 */
export class FetchAllLimitError extends Error {
  readonly count: number;
  readonly limit: number;

  constructor(count: number, limit: number) {
    super(`too many rows to load: ${count} > ${limit}`);
    this.name = 'FetchAllLimitError';
    this.count = count;
    this.limit = limit;
  }
}

function stopAtLimit(count: number): never {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(FETCH_ALL_LIMIT_EVENT, { detail: { count, limit: FETCH_ALL_LIMIT } })
    );
  }
  throw new FetchAllLimitError(count, FETCH_ALL_LIMIT);
}

/**
 * 一覧APIを全件読む。
 *
 * BE-21。一覧は {count, next, results} で返り、1回の件数はサーバーの上限
 * （MAX_PAGE_SIZE）までに制限される。2026-09-08にサーバーが page_size=0（全件）を
 * 既定件数へ置き換えたため、page_size=0 や page_size=<件数> で一度に取る読み方では
 * 選択肢が空になる・上限で切れる。next が無くなるまでページを順に読む。
 *
 * BE-21（2026-10-11 PM 判断）。以前は総数の上限が無かった。総数が FETCH_ALL_LIMIT を超えたら、
 * 最初の応答で止めて FetchAllLimitError を投げる（部分の結果は返さない。選択肢や CSV が黙って
 * 欠けないように）。読む途中で件数が増えて超えたときも同じ。止めたことは画面全体へ知らせ、
 * Layout の案内（reusable/FetchAllLimitNotice）が承認した文を出す。
 *
 * `path` には既存のクエリ条件を含めてよい。
 */
export async function fetchAll<T>(path: string): Promise<T[]> {
  const separator = path.includes('?') ? '&' : '?';
  const rows: T[] = [];
  for (let page = 1; ; page += 1) {
    const response = await axiosInstance.get<Page<T>>(
      `${path}${separator}page=${page}&page_size=${MAX_PAGE_SIZE}`
    );
    if (page === 1 && response.data.count > FETCH_ALL_LIMIT) stopAtLimit(response.data.count);
    rows.push(...response.data.results);
    if (!response.data.next) return rows;
    // 途中で件数が増え、次のページを読むと上限を超える。
    if (rows.length >= FETCH_ALL_LIMIT) {
      stopAtLimit(Math.max(response.data.count, rows.length + 1));
    }
  }
}
